import { FileDrop } from "@countdown/door-editor";
import { Required } from "@countdown/theme";
import { Pencil, Plus, RefreshCw } from "lucide-react";
import { type ReactElement, type SyntheticEvent, useEffect, useRef, useState } from "react";

import { FormField } from "@/components/FormField";
import { Notice } from "@/components/Notice";
import { PageHeading } from "@/components/PageHeading";
import { PlaceholderPicker } from "@/components/PlaceholderPicker";
import { Section } from "@/components/Section";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  type ActionOutcome,
  type BackofficeBrand,
  type BrandSync,
  type ConsentTemplate,
  LOAD_FAILED,
  ROLE_MISSING,
  approveConsentTemplate,
  loadBrands,
  loadConsentTemplates,
  mediaSrc,
  saveBrand,
  syncBrands,
  uploadBrandLogo,
  useResource,
} from "@/lib/api";
import { CONSENT_TYPE_LABELS, can, formatDate, formatDateTime } from "@/lib/labels";

/**
 * Master data of the tenant: the brands (magazines at Pflaum) and the consent wordings, both
 * valid for every action.
 *
 * Tenant admins only, like its navigation entry. The role is checked here
 * because a 403 from the first request cannot be relied on: the brand list is
 * open to action managers, who need it to create an action, so without this
 * they would get forms that fail only on saving. The server enforces the role
 * on every write either way.
 *
 * `brandCatalog` (from the session): whether the brands come from the tenant's catalog. Without
 * one (a reseller) they are created and given a logo here.
 */
export function MasterDataPage({
  granted,
  brandCatalog,
}: {
  readonly granted: readonly string[];
  readonly brandCatalog: boolean;
}): ReactElement {
  if (!can(granted, "brands", "consent_templates")) {
    return (
      <>
        <PageHeading title="Stammdaten" />
        <p className="text-sm text-muted-foreground">{ROLE_MISSING}</p>
      </>
    );
  }

  return (
    <>
      <PageHeading title="Stammdaten" />
      {can(granted, "brands") ? <BrandsSection catalog={brandCatalog} /> : null}
      {can(granted, "consent_templates") ? <ConsentsSection /> : null}
    </>
  );
}

/** "1 neu, 2 geändert": what a sync did, in one line, the problems below it. */
function syncSummary(report: BrandSync): string {
  const parts = [
    `${String(report.created.length)} neu`,
    `${String(report.updated.length)} geändert`,
    `${String(report.unchanged.length)} unverändert`,
  ];
  if (report.skipped_inactive.length > 0) {
    parts.push(`${String(report.skipped_inactive.length)} eingestellt, nicht übernommen`);
  }
  return `Abgleich fertig: ${parts.join(", ")}.`;
}

/**
 * The brands. With a catalog (Pflaum: the magazines) key, name, colour and logo come from the
 * customer portal (D25) and are only shown; what the tool keeps for itself is the sender (D3,
 * D26). Without one they are created here, and each takes its logo in its row.
 */
function BrandsSection({ catalog }: { readonly catalog: boolean }): ReactElement {
  const brands = useResource<BackofficeBrand[]>(loadBrands);
  const [syncing, setSyncing] = useState(false);
  const [report, setReport] = useState<BrandSync | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSync = (): void => {
    setSyncing(true);
    setReport(null);
    setError(null);
    syncBrands()
      .then((result) => {
        if (result.ok) {
          setReport(result.report);
          brands.reload();
        } else {
          setError(result.message);
        }
      })
      .catch((caught: unknown) => {
        console.error("[MasterDataPage] brand sync failed", caught);
        setError(LOAD_FAILED);
      })
      .finally(() => {
        setSyncing(false);
      });
  };

  const rows = brands.status === "ready" ? brands.data : [];

  return (
    <Section
      title={catalog ? "Zeitschriften" : "Marken"}
      description={
        catalog
          ? "Name, Farbe und Logo kommen aus dem Kundenportal. Hier steht nur der Absender."
          : undefined
      }
      actions={
        catalog ? (
          <Button variant="outline" disabled={syncing} onClick={handleSync}>
            <RefreshCw aria-hidden="true" className={syncing ? "animate-spin" : undefined} />
            {syncing ? "Wird abgeglichen …" : "Mit dem Kundenportal abgleichen"}
          </Button>
        ) : brands.status === "ready" ? (
          <CreateBrandDialog
            taken={rows.map((brand) => brand.brand_key)}
            onSaved={brands.reload}
          />
        ) : null
      }
    >
      {report !== null ? (
        <Notice tone={report.problems.length > 0 ? "error" : "success"} className="mb-4">
          <p>{syncSummary(report)}</p>
          {report.problems.length > 0 ? (
            <ul className="mt-1 list-disc pl-5">
              {report.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          ) : null}
        </Notice>
      ) : null}
      {error !== null ? (
        <Notice tone="error" className="mb-4">
          {error}
        </Notice>
      ) : null}

      {brands.status === "loading" || brands.status === "forbidden" ? (
        <p className="text-sm text-muted-foreground">
          {brands.status === "forbidden" ? ROLE_MISSING : "Wird geladen …"}
        </p>
      ) : brands.status === "error" ? (
        <p className="text-sm text-muted-foreground">{LOAD_FAILED}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Logo</TableHead>
              <TableHead>{catalog ? "Zeitschrift" : "Marke"}</TableHead>
              <TableHead>Farbe</TableHead>
              <TableHead>Absender</TableHead>
              <TableHead>Antworten an</TableHead>
              <TableHead>Stand</TableHead>
              <TableHead className="text-right">
                <span className="sr-only">Absender bearbeiten</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((brand) => (
              <TableRow key={brand.brand_id}>
                <TableCell>
                  {brand.logo ? (
                    <img
                      className="h-8 w-auto max-w-24 object-contain"
                      src={mediaSrc(brand.logo.path)}
                      alt={brand.logo.alt_text}
                    />
                  ) : catalog ? (
                    <span className="text-sm text-muted-foreground">keins</span>
                  ) : null}
                  {catalog ? null : <BrandLogoDrop brand={brand} onSaved={brands.reload} />}
                </TableCell>
                <TableCell>
                  <p className="font-medium">{brand.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">{brand.brand_key}</p>
                </TableCell>
                <TableCell>
                  {brand.color_primary ? (
                    <span className="inline-flex items-center gap-2">
                      <span
                        aria-hidden="true"
                        className="inline-block size-4 border border-border"
                        style={{ backgroundColor: brand.color_primary }}
                      />
                      {brand.color_primary}
                    </span>
                  ) : (
                    "keine"
                  )}
                </TableCell>
                <TableCell>
                  {brand.sender_address ?? <StatusBadge tone="warning">Fehlt</StatusBadge>}
                </TableCell>
                <TableCell>{brand.sender_reply_to ?? "keine"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {brand.synced_at ? formatDateTime(brand.synced_at) : "von Hand"}
                </TableCell>
                <TableCell className="text-right">
                  <SenderDialog brand={brand} onSaved={brands.reload} />
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-6 text-center text-muted-foreground">
                  {catalog
                    ? "Noch keine Zeitschrift. »Mit dem Kundenportal abgleichen« holt sie."
                    : "Noch keine Marke. »Marke anlegen« legt eine an."}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}

/**
 * The key of a new brand, from its name: lower case ASCII and hyphens, unique among `taken`.
 * Nobody types it: it only tells brands apart, and a typed key that hit an existing brand
 * would edit that one instead.
 */
export function brandKeyFor(name: string, taken: readonly string[]): string {
  const base =
    name
      .toLowerCase()
      .replaceAll("ä", "ae")
      .replaceAll("ö", "oe")
      .replaceAll("ü", "ue")
      .replaceAll("ß", "ss")
      .normalize("NFKD")
      .replaceAll(/[̀-ͯ]/g, "")
      .replaceAll(/[^a-z0-9]+/g, "-")
      .replaceAll(/^-+|-+$/g, "")
      .slice(0, 40) || "marke";
  let key = base;
  for (let suffix = 2; taken.includes(key); suffix += 1) {
    key = `${base}-${String(suffix)}`;
  }
  return key;
}

/** "Marke anlegen" for a cell without a brand catalog: name, colour and the sender. */
export function CreateBrandDialog({
  taken,
  onSaved,
}: {
  readonly taken: readonly string[];
  readonly onSaved: () => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const value = (name: string): string => {
      const raw = form.get(name);
      return typeof raw === "string" ? raw.trim() : "";
    };
    setBusy(true);
    setError(null);
    saveBrand({
      brand_key: brandKeyFor(value("name"), taken),
      create: true,
      name: value("name"),
      color_primary: value("color_primary"),
      sender_name: value("sender_name"),
      sender_address: value("sender_address"),
    })
      .then((result: ActionOutcome) => {
        if (result.ok) {
          setOpen(false);
          onSaved();
        } else {
          setError(result.message);
        }
      })
      .catch((caught: unknown) => {
        console.error("[MasterDataPage] brand create failed", caught);
        setError(LOAD_FAILED);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        setError(null);
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus aria-hidden="true" />
          Marke anlegen
        </Button>
      </DialogTrigger>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Marke anlegen</DialogTitle>
          <DialogDescription>Das Logo kommt danach in der Liste dazu.</DialogDescription>
        </DialogHeader>
        <form id="marke-neu" onSubmit={handleSubmit}>
          <div className="mt-4 grid gap-2.5">
            <Label htmlFor="marke-name">
              Name
              <Required />
            </Label>
            <Input
              className="bg-background"
              id="marke-name"
              name="name"
              maxLength={200}
              required
            />
          </div>
          <div className="mt-4 grid gap-2.5">
            <Label htmlFor="marke-farbe">Farbe</Label>
            <input
              className="h-9 w-16 border border-input bg-background"
              id="marke-farbe"
              name="color_primary"
              type="color"
              defaultValue="#006d9e"
            />
          </div>
          <FormField label="Absendername" name="sender_name" maxLength={200} />
          <div className="mt-4 grid gap-2.5">
            <Label htmlFor="marke-absender">
              Absender-E-Mail
              <Required />
            </Label>
            <Input
              className="bg-background"
              id="marke-absender"
              name="sender_address"
              type="email"
              maxLength={320}
              required
            />
          </div>
        </form>
        {error !== null ? <Notice tone="error">{error}</Notice> : null}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
            }}
          >
            Abbrechen
          </Button>
          <Button type="submit" form="marke-neu" disabled={busy}>
            {busy ? "Wird angelegt …" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The logo of a brand without a catalog: a file dropped on the zone goes up at once. */
function BrandLogoDrop({
  brand,
  onSaved,
}: {
  readonly brand: BackofficeBrand;
  readonly onSaved: () => void;
}): ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = (file: File | null): void => {
    if (file === null) {
      return;
    }
    setBusy(true);
    setError(null);
    uploadBrandLogo(brand.brand_key, file)
      .then((result) => {
        if (result.ok) {
          onSaved();
        } else {
          setError(result.message);
        }
      })
      .catch((caught: unknown) => {
        console.error("[MasterDataPage] brand logo upload failed", caught);
        setError(LOAD_FAILED);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className="min-w-40">
      <FileDrop
        id={`logo-${brand.brand_key}`}
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        prompt={brand.logo ? "Logo ersetzen" : "Logo hochladen"}
        label={`Logo von ${brand.name}`}
        compact
        disabled={busy}
        onChange={upload}
      />
      {error !== null ? (
        <p className="mt-1 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The sender of one magazine, in a dialog. An emptied reply address is sent as `""`, which
 * the API reads as "clear it"; the sender address, once set, is required: without it the cell
 * sends nothing.
 */
function SenderDialog({
  brand,
  onSaved,
}: {
  readonly brand: BackofficeBrand;
  readonly onSaved: () => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formId = `absender-${brand.brand_key}`;

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const value = (name: string): string => {
      const raw = form.get(name);
      return typeof raw === "string" ? raw.trim() : "";
    };
    const senderAddress = value("sender_address");
    setBusy(true);
    setError(null);
    saveBrand({
      brand_key: brand.brand_key,
      create: false,
      sender_address: senderAddress === "" ? null : senderAddress,
      sender_reply_to: value("sender_reply_to"),
    })
      .then((result: ActionOutcome) => {
        if (result.ok) {
          setOpen(false);
          onSaved();
        } else {
          setError(result.message);
        }
      })
      .catch((caught: unknown) => {
        console.error("[MasterDataPage] brand save failed", caught);
        setError(LOAD_FAILED);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        setError(null);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" aria-label={`Absender von ${brand.name} bearbeiten`}>
          <Pencil aria-hidden="true" />
          Absender
        </Button>
      </DialogTrigger>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Absender von {brand.name}</DialogTitle>
          <DialogDescription>
            Den Namen im Postfach legt jede Aktion selbst fest, unter »Einstellungen«.
          </DialogDescription>
        </DialogHeader>
        <form id={formId} onSubmit={handleSubmit}>
          <FormField
            label="Absender-E-Mail"
            name="sender_address"
            type="email"
            defaultValue={brand.sender_address}
            required={Boolean(brand.sender_address)}
          />
          <FormField
            label="Antwort-E-Mail"
            name="sender_reply_to"
            type="email"
            defaultValue={brand.sender_reply_to}
          />
        </form>
        {error !== null ? <Notice tone="error">{error}</Notice> : null}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
            }}
          >
            Abbrechen
          </Button>
          <Button type="submit" form={formId} disabled={busy}>
            {busy ? "Wird gespeichert …" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConsentsSection(): ReactElement {
  const templates = useResource<ConsentTemplate[]>(loadConsentTemplates);
  const [notice, setNotice] = useState<string | null>(null);

  // Success only: the approved block remounts with the new version and would
  // lose a message of its own. Failures stay at their form (ConsentBlock).
  const approved = (template: ConsentTemplate): void => {
    setNotice(`${CONSENT_TYPE_LABELS[template.consent_type]}: neue Fassung freigegeben.`);
    templates.reload();
  };

  return (
    <Section
      title="Einwilligungen"
      description="Jede Freigabe ist eine neue Fassung. Erteilte Einwilligungen behalten ihren Wortlaut."
    >
      {notice !== null ? (
        <Notice tone="success" className="mb-4">
          {notice}
        </Notice>
      ) : null}
      {templates.status === "forbidden" ? (
        <p className="text-sm text-muted-foreground">{ROLE_MISSING}</p>
      ) : templates.status === "ready" ? (
        <div className="divide-y divide-border">
          {templates.data.map((template) => (
            <ConsentBlock
              // A new version remounts the block with its text.
              key={`${template.consent_type}-${String(template.version)}`}
              template={template}
              onApproved={approved}
            />
          ))}
        </div>
      ) : templates.status === "loading" ? (
        <p className="text-sm text-muted-foreground">Wird geladen …</p>
      ) : (
        <p className="text-sm text-muted-foreground">{LOAD_FAILED}</p>
      )}
    </Section>
  );
}

interface ConsentBlockProps {
  readonly template: ConsentTemplate;
  readonly onApproved: (template: ConsentTemplate) => void;
}

/**
 * A refusal is shown under this block's button, where the click happened: in
 * the section above it would sit off screen for the lower blocks. The button
 * is disabled while busy, which drops the focus, so it gets it back then.
 */
export function ConsentBlock({ template, onApproved }: ConsentBlockProps): ReactElement {
  const current = template.body_template ?? "";
  const [text, setText] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const id = `einwilligung-${template.consent_type}`;

  useEffect(() => {
    if (error !== null && !busy) {
      button.current?.focus();
    }
  }, [error, busy]);

  const approve = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    approveConsentTemplate(template.consent_type, text.trim())
      .then((outcome: ActionOutcome) => {
        if (outcome.ok) {
          onApproved(template);
        } else {
          setError(outcome.message);
        }
      })
      .catch((error_: unknown) => {
        console.error("[MasterDataPage] consent approval failed", error_);
        setError(LOAD_FAILED);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <form className="py-5 first:pt-0 last:pb-0" onSubmit={approve}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <label className="font-bold" htmlFor={id}>
          {CONSENT_TYPE_LABELS[template.consent_type]}
        </label>
        <p className="text-xs text-muted-foreground">
          {template.version === null || template.version === undefined
            ? "Noch keine Fassung freigegeben"
            : `Fassung ${String(template.version)}${
                template.approved_at ? ` vom ${formatDate(template.approved_at)}` : ""
              }`}
        </p>
      </div>
      <Textarea
        ref={field}
        className="mt-2 bg-background"
        id={id}
        rows={6}
        maxLength={20000}
        value={text}
        disabled={busy}
        onChange={(change) => {
          setText(change.target.value);
        }}
      />
      <PlaceholderPicker
        names={template.allowed_placeholders}
        field={field}
        value={text}
        onChange={setText}
        disabled={busy}
      />
      <Button
        ref={button}
        className="mt-3"
        type="submit"
        disabled={busy || text.trim() === "" || text.trim() === current.trim()}
      >
        Neue Fassung freigeben
      </Button>
      {error !== null ? (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}
